import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { generateApiPath } from 'src/utils';
import ModalDialog, {
  ModalCloseHandler,
  ModalCloseReason,
} from 'src/components/ModalDialog';
import { useForm } from 'src/components/Form';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import { useStoreUnitActions } from 'src/redux/helpers';
import solutionsStore, { useSolutionDetails } from '../../redux';
import { API_ASSET_UPDATE_PROPERTY } from '../../constants';
import { solutionEditTwinFormFields } from './SolutionEditTwinFormFields';

interface DisplayData {
  key?: any;
  value: any;
  schema?: string;
}

type Props = {
  open: boolean;
  onClose: ModalCloseHandler;
  id?: string;
  sitesId?: string;
  displayData2: any;
  propertiesData: any;
  level: any;
  setNode?: any;
  setLoader?: any;
};

function SolutionAssetEditModal({
  id,
  sitesId,
  open,
  displayData2,
  propertiesData,
  level,
  setNode,
  setLoader,
  onClose,
}: Props) {
  const actions = useStoreUnitActions(solutionsStore);
  const { showAlert } = useAlert();
  const { solutionId } = useParams();
  const [solutionDetails] = useSolutionDetails();
  const isISA95Standard =
    solutionDetails?.isIsa95Standard || false;

  const initialValues = displayData2
    .filter((item) => item.schema !== '')
    .reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});

  const [formData, setFormData] = useState(initialValues);

  const convertValue = (value) => {
    if (value === true) return true;
    if (value === false) return false;
    return value;
  };

  const updatedPayload = (data) => {
    const validData = Object.entries(data)?.filter(([key]) => key !== '');
    const updatedData = validData.flatMap(([key, value]) => {
      const existingkey = propertiesData.find(
        ([filteredkey]) => filteredkey === key
      );
      const existingValue = existingkey ? existingkey[1] : undefined;

      if (existingValue !== undefined && value === '')
        return [
          {
            op: 'remove',
            path: `/${key}`,
          },
        ];

      if (
        existingkey &&
        existingValue !== value &&
        (existingValue !== null ||
          existingValue !== undefined ||
          existingValue !== '' ||
          existingValue !== 0)
      )
        return [
          {
            op: 'replace',
            path: `/${key}`,
            value: convertValue(value),
          },
        ];
      if (existingValue === undefined && value !== '')
        return [
          {
            op: 'add',
            path: `/${key}`,
            value: convertValue(value),
          },
        ];
      return [];
    });
    return updatedData;
  };

  const [onSubmit, loading] = useStatefulApi((params) =>
    pepApiRequest({
      method: RequestMethod.Put,
      url: generateApiPath(API_ASSET_UPDATE_PROPERTY, {
        solutionId,
        siteId: !isISA95Standard ? '00' : sitesId,
        twinId: id,
      }),
      params: updatedPayload(formData),
    })
      .then((response) => {
        showAlert({
          type: AlertType.Info,
          title: 'Twin Properties Updated',
          text: `${id} twin properties updated successfully`,
        });
        onClose(null, ModalCloseReason.completeAction);
        setLoader(true);
        const timeOutId = setTimeout(() => {
          setNode({
            id,
            relationshipName: 'contains',
            level,
            properties: {},
            children: [],
            siteId: !isISA95Standard ? '00' : sitesId,
          });
          setLoader(false);
        }, 5000);
        return () => {
          if (timeOutId) {
            setLoader(false);
            clearTimeout(timeOutId);
          }
        };
      })
      .catch((error) => {
        setLoader(false);
      })
  );

  const handleChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const dynamicFields = solutionEditTwinFormFields(
    displayData2,
    formData,
    handleChange
  );

  const [formComponent, handleSubmit] = useForm({
    fields: dynamicFields,
    onSubmit,
  });

  return (
    <>
      <ModalDialog
        title="Edit Twin Properties"
        open={open}
        loading={loading}
        onClose={onClose}
        onConfirm={handleSubmit}
        confirmBtnText="Save"
        maxWidth="sm"
        fullWidth
      >
        {formComponent}
      </ModalDialog>
    </>
  );
}

export default SolutionAssetEditModal;
